import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import {
  mergeLocations,
  MergeLocationsImpact,
  DuplicateLocationMember,
} from "api/Locations";

/**
 * MergeLocationsModal \u2014 Session 24
 *
 * Two-phase merge UI mirroring the backend's `?dryRun=1` pattern:
 *
 *   Phase 1: modal opens \u2192 fire dry-run merge immediately, show the
 *            impact summary (records moved per table, profiles
 *            relocated/merged, warnings). No side effects yet.
 *   Phase 2: rep confirms \u2192 fire the real merge, close, refresh caller.
 *
 * The rep can bail out between phases without touching the DB. This is
 * intentional: merges are permanent, and dry-run is cheap enough (same
 * transactional query plan, rolled back at the end).
 *
 * Parent controls source + target selection \u2014 both come in as props
 * because the parent (FindDuplicateLocations page) is where the cluster
 * survivor picker lives. This component just executes and reports.
 */

interface Props {
  source: DuplicateLocationMember;
  target: DuplicateLocationMember;
  onCancel: () => void;
  onDone: () => void; // fired only after a successful non-dry-run merge
}

const formatAddress = (addr: any): string => {
  if (!addr) return "";
  const parts = [addr.line1, addr.city, addr.state].filter(Boolean);
  return parts.join(", ");
};

const styles: Record<string, React.CSSProperties> = {
  intro: {
    fontSize: 13,
    color: "#5D5E62",
    marginTop: 0,
    marginBottom: 16,
    lineHeight: 1.5,
  },
  block: {
    border: "1px solid #E4E7EB",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  blockLabel: {
    fontSize: 11,
    color: "#B0B4BA",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
    marginBottom: 6,
  },
  blockBody: {
    fontSize: 13,
    color: "#111",
  },
  blockAddress: {
    fontSize: 12,
    color: "#5D5E62",
    marginTop: 2,
  },
  arrow: {
    textAlign: "center",
    fontSize: 20,
    color: "#B0B4BA",
    margin: "-4px 0 4px",
  },
  impactList: {
    listStyle: "none",
    padding: 0,
    margin: "8px 0 0 0",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "4px 16px",
  },
  impactItem: {
    fontSize: 13,
    color: "#111",
    display: "flex",
    justifyContent: "space-between",
  },
  impactValue: {
    fontWeight: 600,
  },
  warningsBox: {
    marginTop: 12,
    padding: "10px 12px",
    background: "#FFF4E5",
    border: "1px solid #F5C782",
    borderRadius: 6,
    fontSize: 12,
    color: "#8A5300",
  },
  errorBox: {
    marginTop: 12,
    padding: "10px 12px",
    background: "#FEE2E2",
    border: "1px solid #FCA5A5",
    borderRadius: 6,
    fontSize: 12,
    color: "#991B1B",
  },
  loading: {
    fontSize: 13,
    color: "#5D5E62",
    padding: "20px 0",
    textAlign: "center",
  },
};

const MergeLocationsModal: React.FC<Props> = ({
  source,
  target,
  onCancel,
  onDone,
}) => {
  const [impact, setImpact] = useState<MergeLocationsImpact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"dryrun" | "confirmed" | "committing">(
    "dryrun",
  );

  // Kick off the dry-run on mount. Cheap to compute, safe to run before
  // the rep confirms anything.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      const res = await mergeLocations(source.id, target.id, { dryRun: true });
      if (cancelled) return;
      if ("error" in res) {
        setError(res.error);
        return;
      }
      setImpact(res);
    })();
    return () => {
      cancelled = true;
    };
  }, [source.id, target.id]);

  const handleConfirm = async () => {
    setPhase("committing");
    setError(null);
    const res = await mergeLocations(source.id, target.id, { dryRun: false });
    if ("error" in res) {
      setError(res.error);
      setPhase("dryrun");
      return;
    }
    // Refresh the actual moved/rewritten counts from the executed run.
    setImpact(res);
    setPhase("confirmed");
    // Give the rep a beat to see the final numbers before we close.
    setTimeout(() => onDone(), 800);
  };

  const anyMoved = impact
    ? impact.moved.quoteRequests +
      impact.moved.quotes +
      impact.moved.projects +
      impact.moved.mulchMaps +
      impact.moved.profilesRelocated +
      impact.moved.profilesMerged
    : 0;

  return (
    <Modal isOpen={true} toggle={onCancel} size="lg" centered>
      <ModalHeader toggle={onCancel}>Merge locations</ModalHeader>
      <ModalBody>
        <p style={styles.intro}>
          The <strong>source</strong> Location will be soft-deleted after
          every downstream record (RFQ, Quote, Project, Invoice, PO, Map,
          Profile) is repointed at the <strong>target</strong>. Client
          profiles for matching clients are merged; profiles for unmatched
          clients are relocated intact. The soft-delete is reversible for
          audit, but the record moves are not.
        </p>

        <div style={styles.block}>
          <div style={styles.blockLabel}>Source (will be archived)</div>
          <div style={styles.blockBody}>
            {source.name || "(unnamed)"}
            <div style={styles.blockAddress}>
              {formatAddress(source.address) || "\u2014"}
              {source.latitude != null && source.longitude != null && (
                <>
                  {" \u00b7 "}
                  {source.latitude.toFixed(5)}, {source.longitude.toFixed(5)}
                </>
              )}
            </div>
          </div>
        </div>

        <div style={styles.arrow}>{"\u2193"}</div>

        <div style={styles.block}>
          <div style={styles.blockLabel}>Target (survivor)</div>
          <div style={styles.blockBody}>
            {target.name || "(unnamed)"}
            <div style={styles.blockAddress}>
              {formatAddress(target.address) || "\u2014"}
              {target.latitude != null && target.longitude != null && (
                <>
                  {" \u00b7 "}
                  {target.latitude.toFixed(5)}, {target.longitude.toFixed(5)}
                </>
              )}
            </div>
          </div>
        </div>

        <div style={styles.block}>
          <div style={styles.blockLabel}>
            {phase === "confirmed" ? "Actual" : "Dry-run"} impact
          </div>
          {!impact && !error && (
            <div style={styles.loading}>Computing impact\u2026</div>
          )}
          {impact && (
            <ul style={styles.impactList}>
              <li style={styles.impactItem}>
                <span>Quote requests moved</span>
                <span style={styles.impactValue}>
                  {impact.moved.quoteRequests}
                </span>
              </li>
              <li style={styles.impactItem}>
                <span>Quotes moved</span>
                <span style={styles.impactValue}>{impact.moved.quotes}</span>
              </li>
              <li style={styles.impactItem}>
                <span>Projects moved</span>
                <span style={styles.impactValue}>{impact.moved.projects}</span>
              </li>
              <li style={styles.impactItem}>
                <span>Maps moved</span>
                <span style={styles.impactValue}>{impact.moved.mulchMaps}</span>
              </li>
              <li style={styles.impactItem}>
                <span>Profiles relocated</span>
                <span style={styles.impactValue}>
                  {impact.moved.profilesRelocated}
                </span>
              </li>
              <li style={styles.impactItem}>
                <span>Profiles merged</span>
                <span style={styles.impactValue}>
                  {impact.moved.profilesMerged}
                </span>
              </li>
              <li style={styles.impactItem}>
                <span>Record profile FKs rewritten</span>
                <span style={styles.impactValue}>
                  {impact.moved.recordProfileFksRewritten}
                </span>
              </li>
            </ul>
          )}
          {impact && anyMoved === 0 && (
            <div
              style={{
                fontSize: 12,
                color: "#5D5E62",
                marginTop: 10,
                fontStyle: "italic",
              }}
            >
              Nothing to move \u2014 the source Location has no downstream
              references. Consider deleting it instead of merging.
            </div>
          )}
        </div>

        {impact && Array.isArray(impact.warnings) && impact.warnings.length > 0 && (
          <div style={styles.warningsBox}>
            <strong>Warnings:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {impact.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        {phase === "confirmed" && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              background: "#DCFCE7",
              border: "1px solid #86EFAC",
              borderRadius: 6,
              fontSize: 12,
              color: "#166534",
            }}
          >
            Merge complete. Closing\u2026
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <BasicButton
          type="button"
          scheme="secondary"
          outline
          onClick={onCancel}
          disabled={phase === "committing" || phase === "confirmed"}
          styles={{ margin: "0" }}
          keepText
        >
          Cancel
        </BasicButton>
        <BasicButton
          type="button"
          onClick={handleConfirm}
          disabled={!impact || !!error || phase !== "dryrun"}
          styles={{ margin: "0" }}
          keepText
        >
          {phase === "committing"
            ? "Merging\u2026"
            : phase === "confirmed"
              ? "Merged"
              : "Confirm merge"}
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default MergeLocationsModal;
